<script setup>

    import LwxLotus from '@/Components/LwxLotus.vue'

    import { ref, computed, watch } from 'vue';
    import { useAuthStore, useStaffStore } from '@/Stores'
    import { storeToRefs } from 'pinia'
    import { factory } from '@/Validators';

    const staffStore = useStaffStore()
    const authStore = useAuthStore()

    const { loading, activeStaffs } = storeToRefs(staffStore)
    const { user } = storeToRefs(authStore)

    const emit = defineEmits(['update:modelValue'])

    const props = defineProps({
        loading: {
            type: Boolean,
            default: false
        },
        modelValue: {
            type: Array,
            default: () => []
        },
        validate: {
            type: Array,
            default: () => []
        }
    })

    const selectedStaff = ref([])
    const isValid = ref(true)

    const activeStaffsComputed = computed(() => {
        let staffs = activeStaffs.value.map(staffMember => {
            return {
                ...staffMember,
                name: staffMember.first_name + ' ' + staffMember.last_name
            }
        })
        return staffs
    })

    watch(activeStaffsComputed, (newActiveStaffsComputed) => {
        if (newActiveStaffsComputed.length > 0) {
            if (props.modelValue.length > 0) {
                selectedStaff.value = props.modelValue.map(staff => newActiveStaffsComputed.find(s => s.id === staff.id)).filter(Boolean)
            } else if (selectedStaff.value.length === 0) {
                const currentUser = newActiveStaffsComputed.find(s => s.id === user.value.user_id)
                if (currentUser) {
                    selectedStaff.value = [currentUser]
                }
            }
        }
    }, { immediate: true })

    watch(() => selectedStaff.value, (newValue) => {
        if (props.validate.includes('nullable') && !newValue) {
            isValid.value = true
            return;
        }

        for (let i in props.validate) {

            const validator = factory(props.validate[i])

            if (validator.isValid(newValue) == false) {
                isValid.value = false
                return;
            }

            isValid.value = true
        }

        emit('update:modelValue', newValue)
    }, { immediate: true })
</script>

<template>
    <MultiSelect
        v-model="selectedStaff"
        :options="activeStaffsComputed"
        optionLabel="name"
        placeholder="LifeWorx Team"
        :maxSelectedLabels="4"
        class="w-full min-h-[2rem]"
        :class="!isValid ? 'border-lifeworx-red-500 focus:ring-1 focus:border-lifeworx-red-500 focus:ring-lifeworx-red-500 bg-lifeworx-red-500 bg-opacity-10' : ''"
        :loading="loading"
        filter
        :showToggleAll="false"
        :autoOptionFocus="true"
        :pt="{
            root: 'text-sm',
            overlay: '!z-modal'
        }"
    >
        <template #value="{ value }">
            <span
            class="flex flex-wrap gap-2 items-center">
                <span class="font-semibold text-xs text-stone-400">From:</span>
                <template v-for="(staff, idx) in value" :key="staff.id">
                    <span class="flex items-center gap-1 font-semibold text-xs text-lifeworx-blue-600">
                        <LwxLotus class="w-3 h-3 text-primary" />
                        {{ staff.id === user.user_id ? 'You' : staff.name }}
                    </span>
                </template>
            </span>
        </template>
        <template #option="{ option }">
            <span class="font-semibold text-xs flex items-center gap-1">
                <LwxLotus class="w-3 h-3 text-primary" />
                {{ option.name }} <span v-if="option.id === user.user_id">(You)</span>
            </span>
        </template>
    </MultiSelect>

</template>
